import { EQUIPMENTS_ENDPOINT } from "../../../../../consts";
import { useCRUD } from "../../../../../api/crud";
import { useParams } from "react-router-dom";
import { ReactNode, useEffect, useState } from "react";
import { Equipment } from "../../../../../types/equipments";
import CustomCircularProgress from "../../../../../components/ui/customCircularProgress/customCircularProgress.component";
import { AddressAccordeon } from "../../../../../components/ui/clientAddressAccordion/clientAddressAccordion.component";
import { Box, Typography, Divider, Chip } from "@mui/material";
import { basicInfoContainerStyle } from "../../../../../styles/commonStyles";
import { useTranslation } from "react-i18next";
import { useBreadcrumbs } from "../../../../../components/ui/breadcrumps/breadcrumbsContext";
import PrintIcon from "@mui/icons-material/Print";
import { formatDateTime } from "../../../../../utils";
import { Person4 } from "@mui/icons-material";
import { useHasPermission } from "../../../../../contexts/auth";
import EntityReferenceLink from "../../../../../components/ui/entityReference/entityReferenceLink.component";

type DetailRowProps = {
  label: string;
  children: ReactNode;
  noBorder?: boolean;
};

type EquipmentBasicInfoProps = {
  previewId?: string | number;
};

const DetailRow = ({ label, children, noBorder = false }: DetailRowProps) => (
  <Box
    sx={{
      display: "grid",
      gridTemplateColumns: { xs: "1fr", sm: "200px 1fr" },
      gap: { xs: 0.5, sm: 2 },
      alignItems: "start",
      py: 1.25,
      px: 1.5,
      borderBottom: noBorder ? "none" : "1px solid",
      borderColor: "divider",
    }}
  >
    <Typography variant="body2" sx={{ color: "text.secondary", fontWeight: 600 }}>
      {label}
    </Typography>
    <Box sx={{ minHeight: "24px", display: "flex", alignItems: "center" }}>{children}</Box>
  </Box>
);

const formatTime = (time?: string) => {
  if (!time) {
    return "";
  }

  return time.split(":").slice(0, 2).join(":");
};

export const EquipmentBasicInfo = ({ previewId }: EquipmentBasicInfoProps) => {
  const { id } = useParams();
  const { t } = useTranslation();
  const { get } = useCRUD(EQUIPMENTS_ENDPOINT);
  const { setOverride } = useBreadcrumbs();
  const canViewClients = useHasPermission("settlement.view_client");
  const [equipment, setEquipment] = useState<Equipment | null>(null);
  const entityId = previewId ?? id;

  useEffect(() => {
    const fetchEquipment = async () => {
      if (entityId) {
        const response = await get(entityId);
        setOverride(`/equipment-section/equipments/${entityId}`, response.data ? response.data.full_name : "");
        setEquipment(response.data);
      }
    };

    const handleEquipmentUpdated = () => {
      fetchEquipment();
    };

    window.addEventListener("equipmentUpdated", handleEquipmentUpdated);
    fetchEquipment();
    return () => {
      window.removeEventListener("equipmentUpdated", handleEquipmentUpdated);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entityId]);

  if (!equipment) return <CustomCircularProgress />;
  const configurationValues = Array.isArray(equipment.attribute_values)
    ? equipment.attribute_values
    : [];

  return (
    <Box
      sx={basicInfoContainerStyle}
    >
      {/* Header Section */}
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Typography variant="h5" component="h1" sx={{ fontWeight: 600 }}>
            {equipment.full_name}
          </Typography>
          <Chip
            icon={<PrintIcon />}
            label={equipment.kind_display}
            color="primary"
            size="small"
          />
          <Chip
            icon={<Person4 />}
            label={equipment.ownership_display}
            color="secondary"
            size="small"
          />
        </Box>
      </Box>

      <Divider />

      {/* Basic Information Section */}
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
          {t("Basic Information")}
        </Typography>
        <Box
          sx={{
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 1,
            overflow: "hidden",
          }}
        >
          <DetailRow label={t("Client Name")}>
            <EntityReferenceLink
              entityType="client"
              entityId={equipment.client_id}
              label={equipment.client_name}
              canPreview={canViewClients}
              stopPropagation={false}
            />
          </DetailRow>
          <DetailRow label={t("Producer")}>
            <Typography variant="subtitle1">{equipment.producer ?? "-"}</Typography>
          </DetailRow>
          <DetailRow label={t("Model")}>
            <Typography variant="subtitle1">{equipment.model ?? "-"}</Typography>
          </DetailRow>
          <DetailRow label={t("Serial Number")}>
            <Typography variant="subtitle1">{equipment.serial || "-"}</Typography>
          </DetailRow>
          <DetailRow label={t("Kind")}>
            <Typography variant="subtitle1">{equipment.kind_display || "-"}</Typography>
          </DetailRow>
          <DetailRow label={t("Ownership")}>
            <Typography variant="subtitle1">{equipment.ownership_display || "-"}</Typography>
          </DetailRow>
          <DetailRow label={t("Is installed")}>
            <Typography variant="subtitle1">{equipment.is_installed ? t("Yes") : t("No")}</Typography>
          </DetailRow>
          <DetailRow label={t("Configuration")}>
            {configurationValues.length > 0 ? (
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                {configurationValues.map((value, index) => {
                  const attrName = value.attribute_info?.name;
                  const choiceName = value.choice_info?.name;
                  const note = value.note?.trim();
                  const base =
                    attrName && choiceName
                      ? `${attrName}: ${choiceName}`
                      : choiceName || (value.choice != null ? String(value.choice) : "-");
                  const label = note ? `${base} (${note})` : base;
                  return (
                    <Chip
                      key={`${value.id || value.choice}-${index}`}
                      label={label}
                      size="small"
                      variant="outlined"
                    />
                  );
                })}
              </Box>
            ) : (
              <Typography variant="subtitle1">-</Typography>
            )}
          </DetailRow>
          {equipment.notes && (
            <DetailRow label={t("Notes")} noBorder>
              <Typography
                variant="body2"
                sx={{
                  whiteSpace: "pre-wrap",
                  color: "text.secondary",
                }}
              >
                {equipment.notes}
              </Typography>
            </DetailRow>
          )}
        </Box>
      </Box>

      <Divider />

      {/* Installation Address Section */}
      <AddressAccordeon
        address={equipment.installation_address}
        title={`${t("Installation Address")} & ${t("Opening Hours")}`}
        defaultExpanded
      />

      <Divider />

      {/* Contact Information Section */}
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
          {t("Contact Information")}
        </Typography>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          <Typography variant="subtitle1">
            <strong>{t("Phone Number")}:</strong>{" "}
            {equipment.installation_address?.phone_number || "-"}
          </Typography>
          <Typography variant="subtitle1">
            <strong>{t("Email")}:</strong> {equipment.installation_address?.email || "-"}
          </Typography>
          <Typography variant="subtitle1">
            <strong>{t("Email for counter reminders")}:</strong>{" "}
            {equipment.installation_address?.email_counter_reminders || "-"}
          </Typography>
        </Box>
      </Box>

      {/* Additional Information Section */}
      {(equipment.installation_address?.notes || equipment.installation_address?.opening_hours !== undefined) && (
        <>
          <Divider />
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
              {t("Additional Information")}
            </Typography>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              {equipment.installation_address?.notes && (
                <Box>
                  <Typography variant="subtitle1" sx={{ mb: 1 }}>
                    <strong>{t("Notes")}:</strong>
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      whiteSpace: "pre-wrap",
                      color: "text.secondary",
                      pl: 2,
                    }}
                  >
                    {equipment.installation_address.notes}
                  </Typography>
                </Box>
              )}
              <Box>
                <Typography variant="subtitle1" sx={{ mb: 1 }}>
                  <strong>{t("Opening Hours")}:</strong>
                </Typography>
                <Box sx={{ pl: 2, display: "flex", flexDirection: "column", gap: 0.5 }}>
                  {Array.isArray(equipment.installation_address?.opening_hours) &&
                  equipment.installation_address.opening_hours.length > 0 ? (
                    equipment.installation_address.opening_hours.map((oh: any, idx: number) => {
                      const dayNames: { [key: string]: string } = {
                        "1": t("Monday"),
                        "2": t("Tuesday"),
                        "3": t("Wednesday"),
                        "4": t("Thursday"),
                        "5": t("Friday"),
                        "6": t("Saturday"),
                        "7": t("Sunday"),
                      };
                      return (
                        <Typography
                          key={oh.id || idx}
                          variant="body2"
                          sx={{ color: "text.secondary" }}
                        >
                          {typeof oh === "string"
                            ? oh
                            : `${dayNames[oh.week_day] || oh.week_day}: ${formatTime(oh.from_hour)} - ${formatTime(oh.to_hour)}`}
                        </Typography>
                      );
                    })
                  ) : (
                    <Typography
                      variant="body2"
                      sx={{ color: "text.secondary", fontStyle: "italic" }}
                    >
                      {t("No opening hours")}
                    </Typography>
                  )}
                </Box>
              </Box>
            </Box>
          </Box>
        </>
      )}

      <Divider />

      {/* Metadata Section */}
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
          {t("Metadata")}
        </Typography>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          <Typography variant="body2" color="text.secondary">
            <strong>{t("Created At")}:</strong> {formatDateTime(equipment.created_at)}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            <strong>{t("Updated At")}:</strong> {formatDateTime(equipment.updated_at)}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};
